import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowUpRight,
  BookOpen,
  Briefcase,
  Building2,
  ExternalLink,
  FileText,
  HelpCircle,
  Inbox,
  Settings,
  Users,
} from 'lucide-react'
import { adminApi } from '../api'
import { useAdminAuth } from '../AuthContext'
import { Card, DashboardSkeleton } from '../ui'

type Stats = {
  insights: number
  caseStudies: number
  submissions: number
  team: number
}

const contentItems = [
  { to: '/admin/insights', label: 'Insights', desc: 'Blog posts', icon: BookOpen, tone: 'bg-primary/10 text-primary' },
  { to: '/admin/case-studies', label: 'Case studies', desc: 'Client stories', icon: Briefcase, tone: 'bg-accent/30 text-navy-900' },
  { to: '/admin/team', label: 'Team', desc: 'About page', icon: Users, tone: 'bg-sky-100 text-sky-800' },
  { to: '/admin/clients', label: 'Clients', desc: 'Logo grid', icon: Building2, tone: 'bg-violet-100 text-violet-800' },
]

const siteItems = [
  { to: '/admin/faqs', label: 'FAQs', desc: 'Contact FAQs', icon: HelpCircle, tone: 'bg-emerald-100 text-emerald-800' },
  { to: '/admin/policies', label: 'Policies', desc: 'Legal pages', icon: FileText, tone: 'bg-orange-100 text-orange-800' },
  { to: '/admin/settings', label: 'Settings', desc: 'Site & social', icon: Settings, tone: 'bg-slate-100 text-slate-700' },
  { to: '/admin/submissions', label: 'Leads', desc: 'Form inbox', icon: Inbox, tone: 'bg-rose-100 text-rose-800' },
]

function greeting() {
  const h = new Date().getHours()
  if (h < 12) return 'Good morning'
  if (h < 17) return 'Good afternoon'
  return 'Good evening'
}

export default function HomePage() {
  const { user } = useAdminAuth()
  const firstName = (user?.name || 'Admin').split(/\s+/)[0]
  const [stats, setStats] = useState<Stats | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const [insights, cases, subs, team] = await Promise.all([
          adminApi<{ insights?: unknown[] }>('/api/admin/website/insights'),
          adminApi<{ caseStudies?: unknown[] }>('/api/admin/website/case-studies'),
          adminApi<{ submissions?: unknown[] }>('/api/admin/website/submissions'),
          adminApi<{ team?: unknown[] }>('/api/admin/website/team'),
        ])
        if (cancelled) return
        setStats({
          insights: insights.data.insights?.length ?? 0,
          caseStudies: cases.data.caseStudies?.length ?? 0,
          submissions: subs.data.submissions?.length ?? 0,
          team: team.data.team?.length ?? 0,
        })
      } catch {
        if (!cancelled) setStats({ insights: 0, caseStudies: 0, submissions: 0, team: 0 })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  if (!stats) {
    return <DashboardSkeleton />
  }

  return (
    <div className="space-y-6 sm:space-y-8 pb-2">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-[1.5rem] border border-primary/10 bg-navy-900 px-5 py-6 sm:px-7 sm:py-8 text-white shadow-card-dark">
        <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-primary/40 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-12 left-8 h-36 w-36 rounded-full bg-accent/30 blur-3xl" />
        <div className="relative">
          <p className="text-[0.7rem] font-bold uppercase tracking-[0.16em] text-accent">Website CMS</p>
          <h1 className="mt-2 font-display text-2xl sm:text-3xl font-bold tracking-tight">
            {greeting()}, {firstName}
          </h1>
          <p className="mt-2 max-w-lg text-sm sm:text-base text-slate-300">
            Manage digisutra.in content. Edits go live on the public site.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <Link
              to="/admin/insights"
              className="inline-flex min-h-[44px] items-center justify-center rounded-2xl bg-accent px-4 py-2.5 text-sm font-semibold text-navy-900 shadow-[0_4px_16px_-2px_rgba(255,215,0,0.4)] active:scale-[0.98] transition"
            >
              New insight
            </Link>
            <Link
              to="/admin/submissions"
              className="inline-flex min-h-[44px] items-center justify-center rounded-2xl border border-white/20 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white backdrop-blur active:scale-[0.98] transition"
            >
              View leads
            </Link>
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-2xl px-3 py-2.5 text-sm font-medium text-slate-300 hover:text-accent transition"
            >
              Open site
              <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            </a>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Insights" value={stats?.insights} to="/admin/insights" />
        <StatCard label="Case studies" value={stats?.caseStudies} to="/admin/case-studies" />
        <StatCard label="Team" value={stats?.team} to="/admin/team" />
        <StatCard label="Leads" value={stats?.submissions} to="/admin/submissions" highlight />
      </section>

      {/* Content modules */}
      <section className="space-y-3">
        <SectionLabel>Content</SectionLabel>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {contentItems.map((i) => (
            <ModuleTile key={i.to} {...i} />
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <SectionLabel>Site</SectionLabel>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {siteItems.map((i) => (
            <ModuleTile key={i.to} {...i} />
          ))}
        </div>
      </section>
    </div>
  )
}

function SectionLabel({ children }: { children: string }) {
  return (
    <h2 className="text-[0.7rem] font-bold uppercase tracking-[0.16em] text-primary/70 px-0.5">
      {children}
    </h2>
  )
}

function StatCard({
  label,
  value,
  to,
  highlight,
}: {
  label: string
  value: number | undefined
  to: string
  highlight?: boolean
}) {
  return (
    <Link to={to} className="block active:scale-[0.98] transition">
      <Card
        className={`!p-4 ${highlight ? 'border-accent/40 bg-accent/10' : ''}`}
      >
        <div className="text-[0.65rem] font-bold uppercase tracking-[0.12em] text-slate-500">{label}</div>
        <div className="mt-1 font-display text-2xl sm:text-3xl font-bold text-navy-900 tabular-nums">
          {value === undefined ? '—' : value}
        </div>
      </Card>
    </Link>
  )
}

function ModuleTile({
  to,
  label,
  desc,
  icon: Icon,
  tone,
}: {
  to: string
  label: string
  desc: string
  icon: typeof BookOpen
  tone: string
}) {
  return (
    <Link to={to} className="group block active:scale-[0.98] transition">
      <Card className="h-full !p-4 sm:!p-5 transition-all duration-300 group-hover:border-accent/40 group-hover:shadow-card-hover">
        <div className="flex items-start justify-between gap-2">
          <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${tone}`}>
            <Icon className="h-5 w-5" aria-hidden />
          </div>
          <ArrowUpRight className="h-4 w-4 text-slate-300 transition group-hover:text-primary mt-1" aria-hidden />
        </div>
        <div className="mt-3 font-display font-bold text-navy-900 text-[0.95rem] sm:text-base leading-tight">
          {label}
        </div>
        <div className="mt-0.5 text-xs sm:text-sm text-slate-500 leading-snug">{desc}</div>
      </Card>
    </Link>
  )
}
